import React, { useState } from 'react';

const VitalsUpdateModal = ({ isOpen, onClose, currentVitals, onSave }) => {
  const [formData, setFormData] = useState({
    bloodPressure: currentVitals.bloodPressure?.value || '',
    heartRate: currentVitals.heartRate?.value || '',
    temperature: currentVitals.temperature?.value || '',
    oxygenSaturation: currentVitals.oxygenSaturation?.value || '',
    weight: currentVitals.weight?.value || '',
    bloodGlucose: currentVitals.bloodGlucose?.value || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create updated vitals object with today's date and analyze status
    const updatedVitals = {
      bloodPressure: { 
        value: formData.bloodPressure, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: analyzeBloodPressure(formData.bloodPressure)
      },
      heartRate: { 
        value: formData.heartRate, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: analyzeHeartRate(formData.heartRate) 
      },
      temperature: { 
        value: formData.temperature, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: analyzeTemperature(formData.temperature)
      },
      oxygenSaturation: { 
        value: formData.oxygenSaturation, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: analyzeOxygenSaturation(formData.oxygenSaturation)
      },
      weight: { 
        value: formData.weight, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: 'normal' // Simplified - would need patient history for meaningful analysis
      },
      bloodGlucose: { 
        value: formData.bloodGlucose, 
        updatedAt: new Date().toISOString().split('T')[0],
        status: analyzeBloodGlucose(formData.bloodGlucose)
      }
    };
    
    onSave(updatedVitals);
    onClose();
  };
  
  // Simple analyzers for vitals (these would be more sophisticated in a real app)
  const analyzeBloodPressure = (bp) => {
    const parts = bp.split('/');
    if (parts.length !== 2) return 'unknown';
    
    const systolic = parseInt(parts[0]);
    const diastolic = parseInt(parts[1]);
    
    if (systolic >= 140 || diastolic >= 90) return 'alert';
    if ((systolic >= 130 && systolic < 140) || (diastolic >= 80 && diastolic < 90)) return 'borderline';
    return 'normal';
  };
  
  const analyzeHeartRate = (hr) => {
    const rate = parseInt(hr);
    if (rate < 60 || rate > 100) return 'alert';
    if (rate < 65 || rate > 90) return 'borderline';
    return 'normal';
  };
  
  const analyzeTemperature = (temp) => {
    const t = parseFloat(temp);
    if (t < 97 || t > 99.5) return 'alert';
    if (t < 97.5 || t > 99) return 'borderline';
    return 'normal';
  };
  
  const analyzeOxygenSaturation = (o2) => {
    const sat = parseInt(o2);
    if (sat < 95) return 'alert';
    if (sat < 97) return 'borderline';
    return 'normal';
  };
  
  const analyzeBloodGlucose = (bg) => {
    const glucose = parseInt(bg);
    if (glucose > 125) return 'alert';
    if (glucose >= 100 && glucose <= 125) return 'borderline';
    return 'normal';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">Update Your Vitals</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Blood Pressure (Systolic/Diastolic)
              </label>
              <input
                type="text"
                name="bloodPressure"
                value={formData.bloodPressure}
                onChange={handleChange}
                placeholder="120/80"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Format: Systolic/Diastolic (e.g., 120/80)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Heart Rate (bpm)
              </label>
              <input
                type="text"
                name="heartRate"
                value={formData.heartRate}
                onChange={handleChange}
                placeholder="70"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Temperature (°F)
              </label>
              <input
                type="text"
                name="temperature"
                value={formData.temperature}
                onChange={handleChange}
                placeholder="98.6"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Oxygen Saturation (%)
              </label>
              <input
                type="text"
                name="oxygenSaturation"
                value={formData.oxygenSaturation}
                onChange={handleChange}
                placeholder="98"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Weight (lbs)
              </label>
              <input
                type="text"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                placeholder="150"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Blood Glucose (mg/dL)
              </label>
              <input
                type="text"
                name="bloodGlucose"
                value={formData.bloodGlucose}
                onChange={handleChange}
                placeholder="100"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VitalsUpdateModal;